/* eslint-disable react-hooks/rules-of-hooks */
import { SimpleOption } from "@/components/governance/search/AmbientFacilitiesContext";
import { FieldMappingResponseDto, MaskingRule, PrimaryClassification, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { ZUtils } from "./FieldsMappingList.col.validate";
import { numericMaskHelperTexts } from "@/components/governance/fieldEdit/FieldEdit";
import { FormControl, Select, MenuItem, FormHelperText, Box, InputAdornment, FormControlLabel, Checkbox, TextField } from "@mui/material";
import { decideDataTypeMaskingKind } from "@/components/governance/fieldEdit/maskingRuleDataTypesMap";
import { adjustErrorHelperText, couplingValidationRequest } from "./FieldsMappingList.events";


interface MaskingRuleColumnProps {
  fieldDto: FieldMappingResponseDto;
  utils: ZUtils['utils'],
  plainStateUtils: ZUtils['plainStateUtils'],
  hasWritePermit: boolean;
  encryptSettingChangeLocked?: boolean | undefined;
  classificationSelectOptions: SimpleOption[];
}

const MaskingRuleColumn = function comp({
  fieldDto, utils, plainStateUtils, hasWritePermit, encryptSettingChangeLocked, classificationSelectOptions, 
}: MaskingRuleColumnProps
) {
  const { setStates, states } = utils
  const { fieldProps, commonMutate } = plainStateUtils

  const vprops = fieldProps('maskingValue')
  const cvr = couplingValidationRequest(states)
  const h1 = adjustErrorHelperText(states, 'maskingRule', fieldProps, cvr.M)
  const h2 = adjustErrorHelperText(states, 'maskingValue', fieldProps, cvr.M)

  const sndClsId = states?.stage.secondaryClassificationId || -1
  const clss = classificationSelectOptions.find(c => c.key === sndClsId) as any as SecondaryClassificationResponseDto
  
  const maskingKind = decideDataTypeMaskingKind(fieldDto.dataType || '')
  const mrulePath = `${fieldDto.fieldId}.maskingRule`
  const mvalPath = `${fieldDto.fieldId}.maskingValue`

  return (
    <>
      <FormControl error={h1.error}>
        <Select
          disabled={!hasWritePermit || encryptSettingChangeLocked}
          name={mrulePath}
          sx={{ minWidth: 120 }}
          size="small"
          value={states?.stage?.maskingRule || (states?.stage?.maskingRule === MaskingRule.None ? MaskingRule.None : '')}
          onChange={(e, val) => {
            setStates?.(v => {
              if (!commonMutate) return v;
              const v1 = commonMutate('maskingRule', Number(e.target.value))(v)
              v1.touched.maskingRule =
                v1.touched.maskingRule ||
                (v1.stage.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour) !== (states?.stage as any).preserveLast4
              return v1
            })
          }}
        >
          <MenuItem value={MaskingRule.None}>None</MenuItem>
          {clss?.primaryClassification === PrimaryClassification.Public ? null
            : maskingKind === 'string' ?
            <MenuItem value={MaskingRule.ReplaceWithCharacter}>Replace With Character</MenuItem>
            : maskingKind === 'date' ?
            <MenuItem value={MaskingRule.ReplaceWithDefaultDate}>Replace With Default Date</MenuItem>
            : (maskingKind === 'integer_num' || maskingKind === 'non_integer_num') ?
            <MenuItem value={MaskingRule.ReplaceWithDefaultDate}>Replace With Default Value</MenuItem>
            : null
          }
        </Select>
        {h1.error && 
          <FormHelperText id={`${fieldDto.fieldId}-masking-rule-helper-text`}>{h1.helperText}</FormHelperText>}
      </FormControl>
      {(states?.stage.maskingRule || MaskingRule.None) !== MaskingRule.None &&
        <>
          {
            states?.stage.maskingRule !== MaskingRule.ReplaceWithDefaultDate ?
            <Box sx={{ display: 'flex', flexDirection: 'row', mt: 1 }}>
              <Box sx={{ width: 110, mr: 2 }}>
                <span id={`label-c-${fieldDto.fieldId}`} className="sr-only">Character</span>
                <TextField
                  disabled={!hasWritePermit || encryptSettingChangeLocked}
                  select
                  error={vprops?.error}
                  helperText={vprops?.helperText}
                  size="small"
                  name={mvalPath}
                  value={states?.stage.maskingValue || ''}
                  InputProps={{
                    startAdornment: <InputAdornment aria-label="Character" position="start" sx={{mr:2}}>Char:</InputAdornment>,
                  }}
                  inputProps={{
                    labelId: `label-c-${fieldDto.fieldId}`
                  }}
                  onChange={vprops?.onChange}
                >
                  {(maskingKind === 'string' ? ['*', '-'] : []).map(s => 
                    <MenuItem key={s} value={s}>{s}</MenuItem>                          
                  )}
                </TextField>
              </Box>
              <FormControlLabel label="Preserve Last 4" 
                control={
                  <Checkbox 
                    disabled={!hasWritePermit || encryptSettingChangeLocked}
                    checked={(states?.stage as any).preserveLast4 ?? false} 
                    onChange={(e, checked) => { 
                      setStates?.(v => {
                        const v1 = {...v}
                        ;(v1.stage as any).preserveLast4 = checked
                        v1.touched.maskingRule =
                          (fieldDto.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour) !== checked
                        return v1
                      })
                    }} 
                  />
                } 
              />
            </Box> :
            <>
              <label htmlFor={`label-mv-${fieldDto.fieldId}`} className="sr-only">Masking Value</label>
              <TextField 
                disabled={!hasWritePermit || encryptSettingChangeLocked}
                id={`label-mv-${fieldDto.fieldId}`} 
                sx={{ mt: 1, maxWidth: 230 }}
                size="small"
                name={mvalPath}
                helperText={
                    h2.error               ? h2.helperText
                  : maskingKind === 'date' ? "Date in format like: 1970-01-01"
                  : (maskingKind === 'integer_num' || maskingKind === 'non_integer_num') 
                    ? numericMaskHelperTexts[(fieldDto.dataType || '').toLowerCase()]
                    : null
                }
                InputProps={{
                  "aria-label": 'Masking Value'
                }}
                autoComplete="off"
                error={h2.error}
                value={(states.stage.maskingValue || '')}
                onChange={vprops?.onChange}
              />
            </>
          }
        </>
      }
    </>
  )
}

export default MaskingRuleColumn;